import moment, { Moment } from 'moment';
import { toMomentDate } from 'shovel-lib/utils/timeUtils';

const toToDoTask = (task: any): any => {
  const {
    taskId,
    title,
    subtitle,
    type,
    courseId,
    courseName,
    colorHex,
    timeNeed,
    timeHave,
    timeSpent,
    timePlanned,
    daysToStartAhead,
    dueDate,
    duration,
    categoryId,
    categoryName,
    emoji,
    priority,
    readingSourceName,
    readingSourceId
  } = task;
  return {
    taskId,
    title,
    subtitle,
    type,
    courseId,
    courseName,
    colorHex,
    priority,
    timeNeed,
    timeHave,
    timeSpent,
    timePlanned,
    daysToStartAhead,
    duration,
    categoryId,
    categoryName,
    emoji,
    readingSourceName,
    readingSourceId,
    due: toMomentDate(dueDate)
  };
};

const toDoLaterTask = (task: any): any => {
  const {
    taskId,
    title,
    subtitle,
    dueDate,
    type,
    courseId,
    courseName,
    colorHex,
    timeNeed,
    timeSpent,
    timePlanned,
    daysToStartAhead,
    duration,
    priority,
    categoryId,
    categoryName,
    emoji,
    readingSourceId,
    readingSourceName
  } = task;
  return {
    taskId,
    title,
    subtitle,
    type,
    priority,
    courseId,
    courseName,
    colorHex,
    timeNeed,
    timeSpent,
    timePlanned,
    daysToStartAhead,
    duration,
    categoryId,
    categoryName,
    emoji,
    readingSourceId,
    readingSourceName,
    due: toMomentDate(dueDate)
  };
};

const toCompletedTask = (task: any): any => {
  const {
    taskId,
    title,
    subtitle,
    type,
    dueDate,
    courseId,
    courseName,
    colorHex,
    timeSpent,
    completedOn,
    daysToStartAhead,
    duration,
    priority,
    categoryId,
    categoryName,
    emoji,
    readingSourceId,
    readingSourceName
  } = task;
  return {
    taskId,
    title,
    subtitle,
    type,
    priority,
    courseId,
    courseName,
    colorHex,
    timeSpent,
    completedOn,
    duration,
    categoryName,
    categoryId,
    emoji,
    readingSourceName,
    readingSourceId,
    due: dueDate ? toMomentDate(dueDate) : null,
    daysToStartAhead
  };
};

const toNoDueDateTask = (task: any): any => {
  const {
    taskId,
    title,
    subtitle,
    type,
    courseId,
    courseName,
    colorHex,
    timeSpent,
    daysToStartAhead,
    duration,
    categoryName,
    categoryId,
    emoji,
    priority,
    timeNeed,
    timeHave,
    timePlanned,
    readingSourceId,
    readingSourceName
  } = task;
  return {
    taskId,
    title,
    subtitle,
    type,
    priority,
    courseId,
    courseName,
    colorHex,
    timeNeed,
    timeHave,
    timeSpent,
    timePlanned,
    daysToStartAhead,
    duration,
    due: null,
    categoryId,
    categoryName,
    emoji,
    readingSourceId,
    readingSourceName
  };
};

const toRecentlyCreatedTask = (task: any): any => {
  const {
    taskId,
    title,
    subtitle,
    type,
    courseId,
    courseName,
    colorHex,
    timeNeed,
    timeHave,
    timeSpent,
    priority,
    timePlanned,
    daysToStartAhead,
    dueDate,
    duration,
    categoryId,
    categoryName,
    emoji,
    readingSourceName,
    readingSourceId,
    createdAt
  } = task;
  return {
    taskId,
    title,
    subtitle,
    type,
    priority,
    courseId,
    courseName,
    colorHex,
    timeNeed,
    timeHave,
    timeSpent,
    timePlanned,
    daysToStartAhead,
    duration,
    categoryId,
    categoryName,
    emoji,
    readingSourceName,
    readingSourceId,
    due: dueDate ? toMomentDate(dueDate) : null,
    createdAt
  };
};

// The same moment as shown: the same instant, in the same zone. Invalid ones (no date) count as the same.
const sameMoment = (a: Moment, b: Moment) =>
  a.isValid()
    ? b.isValid() && a.valueOf() === b.valueOf() && a.utcOffset() === b.utcOffset() && a.tz() === b.tz()
    : !b.isValid();

const sameValue = (a: any, b: any) => a === b || (moment.isMoment(a) && moment.isMoment(b) && sameMoment(a, b));

// The converters above make flat objects: every field a plain value, except the due date.
const sameTask = (a: object, b: object) => {
  const keys = Object.keys(b);
  return keys.length === Object.keys(a).length && keys.every(key => key in a && sameValue(a[key], b[key]));
};

/**
 * A reloaded list keeps the task objects that came back unchanged, and is the old list itself when nothing in it
 * changed. A card is drawn again only when its task is a new object: saving one task's estimate reloads every list,
 * and each card in the sidebar used to be drawn again several times over for it.
 */
const keepUnchanged = <T extends { taskId: number }>(previous: T[] | undefined, next: T[]): T[] => {
  if (!previous || previous.length === 0) return next;
  const before = new Map(previous.map(task => [task.taskId, task]));
  let unchanged = previous.length === next.length;
  const kept = next.map((task, i) => {
    const old = before.get(task.taskId);
    const result = old && sameTask(old, task) ? old : task;
    if (result !== previous[i]) unchanged = false;
    return result;
  });
  return unchanged ? previous : kept;
};

export { toToDoTask, toDoLaterTask, toCompletedTask, toNoDueDateTask, toRecentlyCreatedTask, keepUnchanged };
